import type { AppLanguage } from "@/lib/appLanguage";
import noTranslations from "@/translations/no";

export type LocaleDict = Record<string, string>;
export type LocaleDictionaries = Record<AppLanguage, LocaleDict>;

const loaded = new Set<AppLanguage>(["no"]);

let dictionaries: LocaleDictionaries = {
  no: noTranslations as LocaleDict,
  en: {},
  uk: {},
  ar: {},
  zh: {},
};

const loaders: Record<Exclude<AppLanguage, "no">, () => Promise<{ default: LocaleDict }>> = {
  en: () => import("@/translations/en"),
  uk: () => import("@/translations/uk"),
  ar: () => import("@/translations/ar"),
  zh: () => import("@/translations/zh"),
};

const inFlight = new Map<AppLanguage, Promise<void>>();

/** Snapshot used by sync t() / resolveTranslation (unloaded locales are empty → fall back to no). */
export function getLocaleDictionaries(): LocaleDictionaries {
  return dictionaries;
}

export function isLocaleLoaded(language: AppLanguage): boolean {
  return loaded.has(language);
}

/** Test helper: reset non-no locales (keeps Norwegian). */
export function __resetLocaleDictionariesForTests(): void {
  loaded.clear();
  loaded.add("no");
  inFlight.clear();
  dictionaries = {
    no: noTranslations as LocaleDict,
    en: {},
    uk: {},
    ar: {},
    zh: {},
  };
}

/**
 * Idempotent dynamic import for a locale chunk.
 * Norwegian is always present (eager).
 */
export function ensureLocaleLoaded(language: AppLanguage): Promise<void> {
  if (loaded.has(language)) return Promise.resolve();

  const existing = inFlight.get(language);
  if (existing) return existing;

  if (language === "no") {
    loaded.add("no");
    return Promise.resolve();
  }

  const promise = loaders[language]()
    .then((mod) => {
      dictionaries = {
        ...dictionaries,
        [language]: mod.default as LocaleDict,
      };
      loaded.add(language);
      inFlight.delete(language);
    })
    .catch((err) => {
      inFlight.delete(language);
      throw err;
    });

  inFlight.set(language, promise);
  return promise;
}
